import { ChevronDown, ChevronUp } from "baseui/icon";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEffectOnce } from "usehooks-ts";

import { AvatarPlaceholder } from "../../components/avatar/AvatarPlaceholder";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  getInitials,
  getInitialsFromFullName,
} from "../../constants/constants";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import { getAllUsers, getTeamWorks } from "../../services/AppService";
import { FormSection } from "../../support/FormSection";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";

const tableHead: { id: number; body: SortOrderT }[] = [
  {
    id: 0,
    body: { name: "Assignee", sort: "asc", column: "Name" },
  },
  {
    id: 1,
    body: { name: "Due this week", sort: "asc", column: "ThisWeek" },
  },
  {
    id: 2,
    body: { name: "Due next week", sort: "asc", column: "NextWeek" },
  },
  {
    id: 3,
    body: { name: "Due later", sort: "asc", column: "DueLater" },
  },
  {
    id: 4,
    body: { name: "All open", sort: "asc", column: "AllOpen" },
  },
  {
    id: 5,
    body: { name: "Completed", sort: "asc", column: "Completed" },
  },
];
type ColumnKey =
  | "Name"
  | "ThisWeek"
  | "NextWeek"
  | "DueLater"
  | "AllOpen"
  | "Completed";
type ColumnName =
  | "Assignee"
  | "Due this week"
  | "Due next week"
  | "Due later"
  | "All open"
  | "Completed";

type SortOrderT = {
  column: ColumnKey;
  name: ColumnName;
  sort: "asc" | "desc";
};
const HomeTeamWork = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { teamWorks, users, isLoading } = useAppSelector(
    (state) => state.appReducer,
  );

  const [selectedSortOrder, setSelectedSortOrder] = React.useState<SortOrderT>({
    column: "Name",
    sort: "asc",
    name: "Assignee",
  });

  const onClickSort = (item: { id: number; body: SortOrderT }) => {
    const isSameColumn = selectedSortOrder.name === item.body.name;

    const newSort: "asc" | "desc" =
      isSameColumn && selectedSortOrder.sort === "asc" ? "desc" : "asc";

    const newSortOrder: SortOrderT = {
      column: item.body.column,
      name: item.body.name,
      sort: newSort,
    };

    setSelectedSortOrder(newSortOrder);
    dispatch(
      getTeamWorks({
        orgId: getLinkedFirm()?.orgId,
        sortOrder: newSortOrder.sort || "asc",
        sortColumn: newSortOrder.column || "Name",
      }),
    );
  };
  useEffect(() => {
    dispatch(
      getTeamWorks({
        orgId: getLinkedFirm()?.orgId,
        sortOrder: "asc",
        sortColumn: "Name",
      }),
    );
  }, []);
  const currentUser = (currentUserName: string) => {
    return users.find((user) => {
      const fullName =
        user.userProfile.firstName + " " + " " + user.userProfile.lastName;
      return fullName === currentUserName;
    });
  };

  if (isLoading) {
    return <LottieLoading />;
  }
  return (
    <FormSection name={"Team"}>
      <div>
        <table className={"w-full"}>
          <thead className={"border-b-[1px] border-gray-300"}>
            <tr>
              {tableHead?.map((item) => (
                <th
                  key={item.id}
                  className={"py-[16px] pl-[16px] pr-[32px] last:pr-[16px]"}>
                  <div
                    className={"flex cursor-pointer"}
                    onClick={() => onClickSort(item)}>
                    <div className={"text-[14px] normal-case"}>
                      {item.body.column}
                    </div>
                    {selectedSortOrder.name === item.body.name && (
                      <div className={"relative left-0 top-[-2px]"}>
                        {selectedSortOrder.sort === "asc" ? (
                          <div className={"absolute"}>
                            <ChevronUp size={26} />
                          </div>
                        ) : (
                          <div className={"absolute"}>
                            <ChevronDown size={26} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={"text-[14px]"}>
            {teamWorks.map((user: any) => (
              <tr
                key={user.id}
                className={"border-b-[1px] border-gray-300 hover:bg-gray-100"}>
                <td className={"px-[16px] py-[8px]"}>
                  <div
                    className={"flex cursor-pointer items-center text-[14px]"}
                    onClick={() => {
                      navigate(
                        `/home/user-work/${user?.userId ? user.userId : ""}`,
                      );
                    }}>
                    <AvatarPlaceholder
                      size={"extra-small"}
                      label={getInitials(user.firstName, user.lastName || "")}
                    />
                    <div
                      className={
                        "link-primary  pl-2 text-sm font-[500] normal-case hover:underline"
                      }>
                      {user.firstName + " " + user.lastName}
                    </div>
                  </div>
                </td>
                <td className={"px-[16px] py-[8px] text-[14px]"}>
                  {user.thisWeek}
                </td>
                <td className={"px-[16px] py-[8px] text-[14px]"}>
                  {user.nextWeek}
                </td>
                <td className={"px-[16px] py-[8px] text-[14px]"}>
                  {user.dueLater}
                </td>
                <td className={"px-[16px] py-[8px] text-[14px]"}>
                  {user.allOpen}
                </td>
                <td className={"px-[16px] py-[8px] text-[14px]"}>
                  {user.completed}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </FormSection>
  );
};

export default HomeTeamWork;
